import type { Message } from '@/features/messages';
import { create } from 'zustand';

export type MessageDirection = 'OLDER' | 'NEWER';

export interface FetchAction {
  type: 'fetchTop' | 'fetchBottom';
  conversationId: string;
  fromMessageId?: string | null;
  pageSize: number;
}

export interface RestoreAction {
  type: 'restore';
  conversationId: string;
  targetMessageId: string;
}

export interface JumpToAction {
  type: 'jumpTo';
  conversationId: string;
  targetMessageId: string;
}

export interface InsertMessageAction {
  type: 'insertMessage';
  conversationId: string;
  message: Message;
}

export interface InitAtBottomAction {
  type: 'scrollToBottom';
  conversationId: string;
}

export type Action = FetchAction | JumpToAction | InsertMessageAction | InitAtBottomAction | RestoreAction;

export interface CursorsState {
  topCursor?: string;
  bottomCursor?: string;
  hasMoreTop: boolean;
  hasMoreBottom: boolean;
}

export interface AnchorState {
  isAtBottom: boolean;
  anchorMessageId?: string;
  pendingScrollTo?: string;
}

export interface StackState extends CursorsState, AnchorState {
  queue: Action[];
  inFlight?: Action;
  pageSize: number;
  abortedCount?: number;
  retryCount?: number;

  dispatchAction: (action: Action) => void;
  consumeNext: (conversationId: string) => Action | undefined;
  completeAction: () => void;
  setCursors: (update: Partial<CursorsState>) => void;
  setAnchors: (update: Partial<AnchorState>) => void;
  setPageSize: (size: number) => void;
  flush: () => void;
}

export const useActionStackStore = create<StackState>((set, get) => ({
  queue: [],
  inFlight: undefined,
  pageSize: 50,
  topCursor: undefined,
  bottomCursor: undefined,
  hasMoreTop: true,
  hasMoreBottom: true,
  isAtBottom: true,
  anchorMessageId: undefined,
  pendingScrollTo: undefined,
  abortedCount: 0,
  retryCount: 0,

  dispatchAction: (action: Action) => {
    const { queue } = get();
    // Simple coalescing: drop duplicate consecutive actions of the same type with same cursor/target
    const last = queue[queue.length - 1];
    if (last && last.type === action.type) {
      if (last.type === 'fetchTop' && action.type === 'fetchTop') {
        set({ queue: [...queue.slice(0, -1), action] });
        return;
      }
      if (last.type === 'fetchBottom' && action.type === 'fetchBottom') {
        set({ queue: [...queue.slice(0, -1), action] });
        return;
      }
      if (last.type === 'jumpTo' && action.type === 'jumpTo' && last.targetMessageId !== action.targetMessageId) {
        set({ queue: [...queue.slice(0, -1), action] });
        return;
      }
    }
    set({ queue: [...queue, action] });
  },

  consumeNext: (conversationId: string) => {
    const { queue } = get();
    if (queue.length === 0) return undefined;
    const [next, ...rest] = queue.filter((action) => action.conversationId === conversationId);
    set({ inFlight: next, queue: rest });
    return next;
  },

  completeAction: () => set({ inFlight: undefined }),

  setCursors: (update: Partial<CursorsState>) => set((s) => ({ ...s, ...update })),
  setAnchors: (update: Partial<AnchorState>) => set((s) => ({ ...s, ...update })),
  setPageSize: (size: number) => set({ pageSize: size }),
  flush: () => set({ queue: [], inFlight: undefined })
}));
